import router from '@/router';
import { Component, Vue, Watch } from 'vue-property-decorator';
import axios from 'axios';

import {
  registerKazoo,
  registerStripe,
  purchaseNumber,
  checkCoupon,
  getPaymentPlans,
  sendCheckoutEmailConfirmation,
  getTaxRates,
  postCheckoutActions,
  createSubscription,
  handleCheckoutDB,
} from '@/api';
import {
  KazooUser,
  CheckoutForm,
  NumberInfo,
  Coupon,
  CheckoutFormErrors,
  PlanInformation,
  AvailableCoupons,
  TaxRates,
  PaymentPlans,
  SelectedPlan,
  CheckoutVerbose,
} from '@/shared/models/api.model';

import ComputerHeaderStyle3 from '@/components/computer-header-style-3/computer-header-style-3.component';
import MobileHeader from '@/components/mobile-header/mobile-header.component';
import TabletHeader from '@/components/tablet-header/tablet-header.component';
import ContactSupport from '@/components/contact-support-component/contact-support.component';

// @ts-ignore
import { Card, createToken } from 'vue-stripe-elements-plus';
import { stripeKey, stripeOptions } from './stripeConfig';
import { countries, provinces, states } from './countryStatesProvinces';

import { formatNumber, formatLocation, emailRegExTest } from '@/shared/utils';
import { supportEmail } from '@/main.ts';

if (!stripeKey) {
  throw new Error('Stripe key is empty, null or undefined!');
}

@Component({
  name: 'checkout',
  components: {
    Card,
    MobileHeader,
    TabletHeader,
    ComputerHeaderStyle3,
    ContactSupport,
  },
})
export default class CheckoutComponent extends Vue {
  // token to cancel api call...
  // currently not in use
  public cancelToken = axios.CancelToken;
  public cancelSource = this.cancelToken.source();

  // for contact support
  public mailLink = `mailto:${supportEmail}`;

  public numberInfo: NumberInfo = JSON.parse(sessionStorage.getItem('numberInfo')!) || null;
  public auth0User = JSON.parse(sessionStorage.getItem('auth0User')!) || null;

  public useSignUpEmail: boolean = true;
  public signUpEmail: string = this.auth0User.email || null;

  public firstName = this.auth0User.given_name || null;
  public lastName = this.auth0User.family_name || null;

  /**
   * -----------------------------------
   * # LEFT PANEL VARIABLES
   * ------------------------------------
   */

  // // FOR DEV
  // public checkoutForm: CheckoutForm = {
  //   address1: '9999 test st',
  //   address2: null,
  //   email: this.signUpEmail ? this.signUpEmail : '',
  //   company: 'Cloudpbx',
  //   name: `${this.firstName} ${this.lastName}`,
  //   country: 'CA',
  //   city: 'Vancouver',
  //   stateProvince: 'BC',
  // };

  public checkoutForm: CheckoutForm = {
    address1: '',
    address2: null,
    email: this.signUpEmail ? this.signUpEmail : '',
    company: '',
    name: `${this.firstName} ${this.lastName}`,
    country: JSON.parse(localStorage.getItem('location')!).country.iso_code || null,
    city: '',
    stateProvince: null,
  };

  // ** Country Variables */
  public countries = countries;
  // disables/enable prov/state and plan selection
  public countrySelected: boolean = false;

  // ** State Province Variables */
  public statesProvinces: Array<{ text: string; value: string }> = [];
  public ProvCityError: string = '';

  // ** Stripe Variables */
  public stripeKey: string | undefined = stripeKey;
  public stripeOptions: any = stripeOptions;
  public fetchStripeDataError: boolean = false;
  public stripeElementErrorMessage = '';

  // ** Credit Card Variables */
  public stripeToken: any = { token: { card: { last4: '' } } };

  // ** Coupon Variables */
  public availableCoupons: AvailableCoupons = [];
  public couponCode: string = '';
  public couponValid: boolean = false;

  public couponInfoMessage: string = '';
  public couponDiscount: string = '';
  public coupon: Coupon = { id: '', discount: 0, type: '', currency: '', name: '' };

  /**
   * --------------------------------------------------------------------------------------------------------------
   * RIGHT PANEL VARIABLES
   * --------------------------------------------------------------------------------------------------------------
   */

  /** Plans Variables */
  public paymentPlans: PaymentPlans = [];
  public applicablePaymentPlans: PaymentPlans = [];
  public selectedPlan: SelectedPlan = [];
  public selectedPlanInfo: PlanInformation = {
    id: '',
    currency: '',
    amount: 0,
    metadata: { description: null },
    interval: '',
    trial_period_days: 0,
  };
  public formattedNumber = '';
  public formattedRateCenter = '';

  /** Description Box Variables */
  public planSelected: boolean = false;

  /** Modal Variables */
  public modalProcessingComplete: boolean = true;
  public openModal: boolean = false;
  public taxRates: TaxRates = [];
  public customerTaxIDs: string[] = [];
  public priceTotal: string = '';

  // running status message of checkout progress
  public modalMessage: string = 'By clicking "OK" you will be agreeing to purchase this plan';

  // ** Error Flags*/
  public checkoutInputErrors: CheckoutFormErrors = {
    InputNameError: false,
    InputEmailError: false,
    InputCompError: false,
    InputStreetError: false,
    InputCityError: false,
    InputProvError: false,
    InputProvCityError: false,
    InputCountryError: false,
    InputPlanError: false,
    InputStripeError: false,
  };

  public checkoutError: boolean = false;
  public checkingOut: boolean = false;

  public progressPercent: number = 0;

  /**
   * ------------------------------------------------------------------------------------------------------------
   * NAME FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */
  /** Name input field error  */
  public checkNameInput() {
    if (!this.checkoutForm.name) {
      this.checkoutInputErrors.InputNameError = true;
      return false;
    }
    this.checkoutInputErrors.InputNameError = false;

    return true;
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * EMAIL FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */

  /** Email input field error  */
  public checkEmailInput() {
    if (!emailRegExTest(this.checkoutForm.email) || !this.checkoutForm.email) {
      this.checkoutInputErrors.InputEmailError = true;
      return false;
    }
    this.checkoutInputErrors.InputEmailError = false;

    return true;
  }

  /** Use sign up email on checkbox tick */
  public toggleBillingEmail() {
    if (this.useSignUpEmail) {
      this.checkoutForm.email = this.signUpEmail;
    }
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * COMPANY NAME FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */
  /** Company input field error  */
  public checkCompanyInput() {
    if (!this.checkoutForm.company) {
      this.checkoutInputErrors.InputCompError = true;
      return false;
    }
    this.checkoutInputErrors.InputCompError = false;

    return true;
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * COUNTRY FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */
  @Watch('checkoutForm.country')
  public checkCountryInput() {
    // needs to reset data dependant on country when country is selected
    if (this.checkoutForm.country) {
      // reset all errors related to fields dependant on country
      this.checkoutInputErrors.InputCountryError = false;
      this.checkoutInputErrors.InputPlanError = false;
      this.checkoutInputErrors.InputProvCityError = false;
      this.checkoutInputErrors.InputProvError = false;
      // reset plan info
      this.planSelected = false;
      this.selectedPlanInfo = {
        id: '',
        currency: '',
        amount: 0,
        metadata: {},
        interval: '',
        trial_period_days: 0,
      };
      // reset selected state/province
      this.checkoutForm.stateProvince = '';
      // resets coupon
      this.coupon = { id: '', discount: 0, type: '', currency: '', name: '' };
      this.couponInfoMessage = '';
      this.couponCode = '';
      // enables prov/state and plan selection
      this.countrySelected = true;
      // displays available prov/states
      this.provincesStates();
      // displays available payment plans
      this.filterPaymentPlans();
    }
  }

  /** Get the selected country's currency
   * @returns 3-letter currency code
   */
  public getCountryCurrency(): string {
    let countryCurrency = '';
    if (this.checkoutForm.country === 'US') {
      countryCurrency = 'USD';
    } else if (this.checkoutForm.country === 'CA') {
      countryCurrency = 'CAD';
    } else if (this.checkoutForm.country === 'EU') {
      countryCurrency = 'EUR';
    }
    return countryCurrency;
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * PROV/STATE CITY FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */

  /** City input field error  */
  public checkCityInput() {
    if (!this.checkoutForm.city) {
      this.checkoutInputErrors.InputCityError = true;
    } else {
      this.checkoutInputErrors.InputCityError = false;
    }
  }

  /** Province State input field error  */
  public checkProvStateInput() {
    if (!this.checkoutForm.stateProvince) {
      this.checkoutInputErrors.InputProvError = true;
    } else {
      this.checkoutInputErrors.InputProvError = false;
    }
  }

  /** Prov/State city error messaging */
  public checkCityProvInput() {
    this.checkProvStateInput();
    this.checkCityInput();
    // if city or prov error
    if (this.checkoutInputErrors.InputCityError || this.checkoutInputErrors.InputProvError) {
      this.checkoutInputErrors.InputProvCityError = true;
      // if only city error => enter a city
      if (this.checkoutInputErrors.InputCityError && !this.checkoutInputErrors.InputProvError) {
        this.ProvCityError = 'Please enter a city';
      }
      // if only prov error => enter a prov
      if (!this.checkoutInputErrors.InputCityError && this.checkoutInputErrors.InputProvError) {
        this.ProvCityError = 'Please select a valid state/province';
      }
      // if city and prov error
      if (this.checkoutInputErrors.InputCityError && this.checkoutInputErrors.InputProvError) {
        this.ProvCityError = 'Please select a valid state/province and city';
      }
      return false;
    }
    this.checkoutInputErrors.InputProvCityError = false;

    return true;
  }

  /** sets options for province dropdown menu */
  public provincesStates() {
    if (this.checkoutForm.country === 'CA') {
      return (this.statesProvinces = provinces);
    }
    if (this.checkoutForm.country === 'US') {
      return (this.statesProvinces = states);
    }
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * ADDRESS FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */
  /** Street Address input field error  */
  public checkStreetInput() {
    if (!this.checkoutForm.address1) {
      this.checkoutInputErrors.InputStreetError = true;
      return false;
    }
    this.checkoutInputErrors.InputStreetError = false;

    return true;
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * STRIPE FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */

  /** Fakes focused styling for Stripe element */
  public handleFocus() {
    if (!this.checkoutInputErrors.InputStripeError) {
      document.getElementsByClassName('stripe-card')[0].setAttribute('style', 'border-color: #85b7d9');
    }
  }

  /** Fakes blur styling for Stripe element */
  public handleBlur() {
    if (!this.checkoutInputErrors.InputStripeError) {
      document.getElementsByClassName('stripe-card')[0].setAttribute('style', 'border-color: #d3d9de');
    }
  }

  public checkStripeInput() {
    const stripeStatus = document.getElementsByClassName('stripe-card')[0].firstElementChild!;
    if (
      stripeStatus.classList.contains('StripeElement--empty') ||
      stripeStatus.classList.contains('StripeElement--invalid')
    ) {
      this.checkoutInputErrors.InputStripeError = true;
      this.stripeElementError();
      return false;
    }

    if (stripeStatus.classList.contains('StripeElement--complete')) {
      this.checkoutInputErrors.InputStripeError = false;
      this.stripeElementError();
      return true;
    }
  }

  public stripeListener(event: any) {
    document.getElementsByClassName('stripe-card')[0].setAttribute('style', 'border-color: #85b7d9');

    if (event.error) {
      this.checkoutInputErrors.InputStripeError = true;
    } else {
      this.checkoutInputErrors.InputStripeError = false;
    }
    this.stripeElementError();
  }

  public stripeElementError() {
    if (this.checkoutInputErrors.InputStripeError) {
      document.getElementsByClassName('stripe-card')[0].classList.add('stripe-card-error');
      document.getElementsByClassName('stripe-card')[0].setAttribute('style', 'border-color: #e0b4b4');
    } else {
      document.getElementsByClassName('stripe-card')[0].classList.remove('stripe-card-error');
    }
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * COUPON FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */

  /** Coupon field: if coupon matches an avaliable coupon */
  @Watch('couponCode')
  public checkCouponValidity() {
    const countryCurrency = this.getCountryCurrency();
    const elem = document.getElementById('couponInfoMessage') as HTMLElement;
    if (this.couponCode.length === 0) {
      return (this.couponInfoMessage = '');
    }
    for (const availableCoupon of this.availableCoupons) {
      if (
        this.couponCode === availableCoupon.name &&
        availableCoupon.currency &&
        countryCurrency === availableCoupon.currency.toUpperCase()
      ) {
        this.coupon = availableCoupon;
        this.getDiscount();
        this.couponInfoMessage = `Success! ${this.couponDiscount} discount is applied`;

        elem.style.setProperty('color', 'green');
        this.couponValid = true;
        return;
      }
      if (this.couponCode === availableCoupon.name && availableCoupon.type === '%') {
        this.coupon = availableCoupon;
        this.getDiscount();
        this.couponInfoMessage = `Success! ${this.couponDiscount} discount is applied`;

        elem.style.setProperty('color', 'green');
        this.couponValid = true;
        return;
      }
    }
    this.couponInfoMessage = `Invalid coupon`;
    elem.style.setProperty('color', '#a23f3d');
    this.couponValid = false;
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * PAYMENT PLAN FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */

  /** formats number */

  public getFormatNumber(numberInfo: NumberInfo) {
    if (!formatNumber(numberInfo.number_to_order)) {
      return 'Pending...';
    }
    return formatNumber(numberInfo.number_to_order);
  }

  /** formats rate center */
  public getFormatRateCenter(numberInfo: NumberInfo) {
    if (!formatLocation(numberInfo.location)) {
      return 'Pending...';
    }
    return formatLocation(numberInfo.location);
  }

  /** Filters plans based on country currency */
  public filterPaymentPlans() {
    this.applicablePaymentPlans = [];
    for (const plan of this.paymentPlans) {
      if (plan.value[0].currency.toUpperCase() === this.getCountryCurrency()) {
        this.applicablePaymentPlans.push(plan);
      }
    }
  }

  /** When plan is selected */
  @Watch('selectedPlan')
  public populatePlanInfo() {
    if (this.selectedPlan.length > 0) {
      this.checkoutInputErrors.InputPlanError = false;
      this.selectedPlanInfo = this.selectedPlan[0];
      this.planSelected = true;
      this.checkoutInputErrors.InputPlanError = false;
      this.selectedPlanInfo.currency = this.selectedPlanInfo.currency.toUpperCase();
    }
  }

  /** Error handling for payment plans */
  public checkPlanInput() {
    if (!this.selectedPlanInfo.id) {
      this.checkoutInputErrors.InputPlanError = true;
      return false;
    } else {
      this.checkoutInputErrors.InputPlanError = false;
      return true;
    }
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * DISPLAYING PRICES ON MODAL FUNCTIONS
   * -------------------------------------------------------------------------------------------------------------------
   */

  /** Processed discouns of coupons */
  public getDiscount() {
    if (this.coupon.type === '$') {
      const couponDiscount = parseFloat((this.coupon.discount / 100).toFixed(2)).toFixed(2);
      this.couponDiscount = `$${couponDiscount}`;
    } else if (this.coupon.type === '%') {
      this.couponDiscount = `${this.coupon.discount}%`;
    }
  }

  /** formats street address */
  get format_address() {
    if (!this.checkoutForm.address2) {
      this.checkoutForm.address2 = '';
    }
    const addressString = `${this.checkoutForm.address1} ${this.checkoutForm.address2} | ${this.checkoutForm.city} ${this.checkoutForm.stateProvince}`;
    return addressString.toUpperCase();
  }

  /** Does price calculations and outputs onto modal */
  public priceBreakdown() {
    const applicableTaxes = this.filterTaxRates();

    const checkoutDescription = document.getElementById('price_breakdown') as HTMLElement;
    const checkoutDescriptionLeft = checkoutDescription.childNodes[0] as HTMLElement;
    const checkoutDescriptionRight = checkoutDescription.childNodes[1] as HTMLElement;

    const planPrice = this.selectedPlanInfo.amount / 100;

    checkoutDescriptionLeft.innerHTML = 'Plan price';
    checkoutDescriptionRight.innerHTML = `$${planPrice.toString()}`;

    let couponDiscountTotal: number = 0;

    // Calculating coupon discount
    if (this.coupon.type) {
      if (this.coupon.type === '%') {
        couponDiscountTotal = parseFloat((planPrice * (this.coupon.discount / 100)).toFixed(2));
      } else if (this.coupon.type === '$') {
        couponDiscountTotal = parseFloat((this.coupon.discount / 100).toFixed(2));
      }
      checkoutDescriptionLeft.innerHTML += '<br />Coupon';
      checkoutDescriptionRight.innerHTML += `<br />-$${couponDiscountTotal.toFixed(2)}`;
    }
    const priceBeforeTax = planPrice - couponDiscountTotal;

    // Calculating applicable taxes
    let taxCost = 0;
    for (const applicableTax of applicableTaxes) {
      checkoutDescriptionLeft.innerHTML += `<br />${applicableTax.display_name} (${applicableTax.percentage}%)`;
      const tax = parseFloat((priceBeforeTax * (applicableTax.percentage / 100)).toFixed(2));

      checkoutDescriptionRight.innerHTML += `<br />$${tax.toFixed(2)}`;
      taxCost += tax;
    }

    // Total
    let total = (priceBeforeTax + taxCost).toFixed(2);
    if (parseFloat(total) < 0) {
      total = '0.00';
    }
    this.priceTotal = `$${total}`;
  }

  /** gets applicable tax rates based on province
   * @returns list of applicable tax objects
   */
  public filterTaxRates() {
    // taxes that a user is subjected to
    const stripeTaxObject = [];

    // for UI
    const applicableTaxes: TaxRates = [];
    for (const taxRate of this.taxRates) {
      if (this.checkoutForm.stateProvince === taxRate.jurisdiction) {
        applicableTaxes.push(taxRate);
        stripeTaxObject.push(taxRate.taxID);
      }
    }
    // for stripe user creation
    this.customerTaxIDs = stripeTaxObject;
    return applicableTaxes;
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * MODAL FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */

  /**
   * Open Modal function
   * Only opens if no errors on required fields
   */
  public async showModal() {
    if (this.checkCheckoutFields() === true) {
      this.modalProcessingComplete = false;
      this.priceBreakdown();
      this.openModal = true;
      this.modalProcessingComplete = true;
    }
  }
  /** To cancel an API call */

  // public async cancel() {
  //   this.cancelSource.cancel('Operation canceled by the user.');
  //   this.processingComplete = true;
  //   this.openModal = !this.openModal;
  // }

  public checkCheckoutFields(): boolean {
    let validFields = 0;

    // (validFields = validFields); to fix build warnings
    this.checkNameInput() ? (validFields += 1) : (validFields = validFields);
    this.checkEmailInput() ? (validFields += 1) : (validFields = validFields);
    this.checkCompanyInput() ? (validFields += 1) : (validFields = validFields);
    this.checkCityProvInput() ? (validFields += 1) : (validFields = validFields);
    this.checkStreetInput() ? (validFields += 1) : (validFields = validFields);
    this.checkPlanInput() ? (validFields += 1) : (validFields = validFields);
    this.checkStripeInput() ? (validFields += 1) : (validFields = validFields);

    if (validFields === 7) {
      return true;
    }
    return false;
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * CHECKOUT FUNCTIONS
   * ------------------------------------------------------------------------------------------------------------
   */

  public handlePostAPICall(APIResponse: any, API: string) {
    if (APIResponse.error) {
      console.log(APIResponse.error);
      switch (API) {
        case 'StripeAccount':
          this.modalMessage = `Billing account creation failed... please review your billing details or contact us at ${supportEmail}`;
          break;
        case 'StripeSubscription':
          this.modalMessage = `Creating a subscription failed... Please contact us at ${supportEmail}`;
          break;
        case 'Kazoo':
          this.modalMessage = `Kazoo account creation failed... please contact us at ${supportEmail}`;
          break;
        case 'Purchase':
          this.modalMessage = `Failed to purchase number... Please contact us at ${supportEmail}`;
          break;
        case 'DB':
          this.modalMessage = `Failed to make database connections... Please contact us at ${supportEmail}`;
          break;
      }

      this.checkoutError = true;
      this.checkingOut = false;

      return false;
    }
    return true;
  }

  /**
   * Handles Stripe CC submission and change button text to indicate successful checkout
   */
  public async checkout() {
    // set timezone to maxmind or defautl to browser
    const timezone =
      JSON.parse(localStorage.getItem('location')!).timezone !== null
        ? JSON.parse(localStorage.getItem('location')!).timezone
        : Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.stripeToken = await createToken();
    // resets cancel token... not in use
    this.cancelSource = this.cancelToken.source();

    this.modalMessage = '';
    this.checkingOut = true;
    this.checkoutError = false;

    const checkoutVerbose: CheckoutVerbose = {
      stripeId: '',
      kazooUserId: '',
      kazooAccountId: '',
      numberPurchased: '',
      auth0userId: this.auth0User.user_id ? this.auth0User.user_id : this.auth0User.sub,
      invited: false,
      portNumber: sessionStorage.getItem('portNumber') === 'true' || false,
    };

    // STEP 1: USE STRIPE API TO CREATE STRIPE USER
    this.progressPercent = 10;
    this.modalMessage = 'Creating your billing account...';
    const stripeUserResponse = await registerStripe<any>(
      this.stripeToken,
      this.checkoutForm,
      this.numberInfo.number,
      this.cancelSource.token,
    );
    if (!this.handlePostAPICall(stripeUserResponse, 'StripeAccount')) {
      return;
    } else {
      checkoutVerbose.stripeId = stripeUserResponse.data.id;
    }

    // STEP 2: USE STRIPE API TO ADD STRIPE SUBSCRIPTION TO USER // Create stripe subscription
    this.progressPercent = 33;
    this.modalMessage = 'Adding your subscription...';
    const stripeSubscriptionResponse = await createSubscription<any>(
      stripeUserResponse.data.id,
      this.selectedPlanInfo.id,
      this.coupon.id,
      this.customerTaxIDs,
      this.cancelSource.token,
    );

    console.log(stripeSubscriptionResponse);
    if (!this.handlePostAPICall(stripeSubscriptionResponse, 'StripeSubscription')) {
      return;
    }

    // STEP 3: USE KAZOO API TO CREATE KAZOO ACCOUNT AND USER
    this.progressPercent = 55;
    this.modalMessage = 'Creating your calling account...';
    const kazooResponse = await registerKazoo<any>(this.checkoutForm, timezone, null);
    if (!this.handlePostAPICall(kazooResponse, 'Kazoo')) {
      return;
    } else {
      checkoutVerbose.kazooUserId = kazooResponse.data.kazoo_user_id;
      checkoutVerbose.kazooAccountId = kazooResponse.data.kazoo_account_id;
    }

    // STEP 4: PURCHASE NUMBER
    let purchaseNumberResponse: any = '';
    this.progressPercent = 72;

    // while (retries < maxRetries || !purchaseSuccess) {
    // this.modalMessage = 'Purchasing your new Corvum number...';
    if (this.numberInfo && this.numberInfo.api) {
      // Purchase it
      console.log(this.numberInfo);

      purchaseNumberResponse = await purchaseNumber(this.numberInfo, this.signUpEmail, this.cancelSource.token);
      // If Error, Find a new number and purchase it...
      //   if (purchaseNumberResponse.error) {
      //     sessionStorage.setItem('numberInfo', JSON.stringify(purchaseNumberResponse.data));
      //     this.numberInfo = JSON.parse(sessionStorage.getItem('numberInfo')!);
      //     this.modalMessage = `Failed purchasing original number... \
      //       Found new number ${this.numberInfo.number_to_order}`;
      //     retries++;
      //     // purchase was successful
      // }
    }
    if (!this.handlePostAPICall(purchaseNumberResponse, 'Purchase')) {
      return;
    } else {
      checkoutVerbose.numberPurchased = this.numberInfo.number_to_order;
    }

    // STEP 5: DB QUERY: CREATE ALL CRDB FIELDS IN ONE CALL
    // return fail if >=1 creation has failed
    this.progressPercent = 93;
    this.modalMessage = 'Creating database connections...';

    /**
     * Handles DB commits for:
     *    kazoo_changes,
     *    stripe_changes,
     *    number_changes,
     *    auth0_changes,
     *    domain_changes,
     */
    const DBResponse = await handleCheckoutDB<any>(
      stripeUserResponse.data,
      stripeSubscriptionResponse.data,
      kazooResponse.data,
      this.numberInfo,
      this.auth0User,
      checkoutVerbose.invited,
      timezone,

      this.cancelSource.token,
    );
    if (!this.handlePostAPICall(DBResponse, 'DB')) {
      return;
    }

    // STEP 6: POST CHECKOUT ACTIONS

    // list of async functions to wait on
    const promisesToResolve = [];
    if (!this.checkoutError) {
      promisesToResolve.push(
        // internal email for successful client onboard info
        sendCheckoutEmailConfirmation<any>(
          this.numberInfo.number_to_order,
          this.signUpEmail,
          this.checkoutForm.name,
          checkoutVerbose,
        ),
      );
      // registers user to AC
      // TODO: MOVE THIS TO DESTORY OF CHECKOUT-CONFIRMATION PAGE SO WE ONLY UPDATE ONCE
      promisesToResolve.push(
        postCheckoutActions<any>(
          this.numberInfo.number_to_order,
          this.checkoutForm,
          DBResponse.data.created_domain || null,
          this.signUpEmail,
          checkoutVerbose.invited,
          DBResponse.data.polaris_account_id,
        ),
      );
      // ensures functions are done before pushing to next page
      Promise.all(promisesToResolve).then(() => {
        this.progressPercent = 90;
        console.log('I sent some emails.');
      });
    }
    this.checkingOut = false;
    router.push('checkoutconfirmation');
  }

  /**
   * ------------------------------------------------------------------------------------------------------
   * LIFECYCLE HOOKS
   * ------------------------------------------------------------------------------------------------------
   */

  /** Loads in Coupons, Plans and TaxRates  */
  public mounted() {
    this.formattedNumber = this.getFormatNumber(this.numberInfo);
    this.formattedRateCenter = this.getFormatRateCenter(this.numberInfo);
    // gets all coupons from stripe
    checkCoupon<AvailableCoupons>().then(({ data, error }) => {
      if (!error) {
        this.availableCoupons = data;
      }
    });
    // gets all plans from stripe
    getPaymentPlans<PaymentPlans>().then(({ data, error }) => {
      if (!error) {
        this.paymentPlans = data;
        // gets country then sets prov/state and filters payment plans based on currency
        if (this.checkoutForm.country) {
          this.provincesStates();
          this.filterPaymentPlans();
          this.checkCountryInput();
        }
      } else {
        this.fetchStripeDataError = true;
      }
    });

    // gets all taxes from stripe
    getTaxRates<TaxRates>().then(({ data, error }) => {
      if (!error) {
        this.taxRates = data;
      } else {
        this.fetchStripeDataError = true;
      }
    });
  }
}
